import React from 'react';
import {connect} from 'react-redux';
import {Grid, Header} from 'semantic-ui-react';
import _ from 'lodash';
import {generateTimeId} from 'common/utils';
import {produceAppliedDiff} from 'framework/collaboration/diff.utils';
import {
  requestDetailsChangesToDisplayString,
  diffItem,
  displayError,
  createChangeObj,
  calculatePeriod,
  extractChangeParamsFromCfg,
} from 'common/requestDetails/utils';
import {produce} from 'immer';
import {TextAreaField} from 'app-ui-componentlibrary/dist/go/components/common/FormControls';
import SimpleShared from 'go/components/modules/requestDetails/v3/items/shared/SimpleShared';
import {
  BOC,
  BOC_NAMES,
  DESCRIPTION,
  PRICE,
  QUANTITY,
  CHANGES_TYPES,
  UNIT_OF_MEASURE,
  PRE_AWARD,
  VALUE_CHANGE_FORMAT,
  EXPLAIN_OTHER,
  NAME,
  VALUE_CHANGE_DISPLAY,
  PHASED_PROJECT,
  COST_CLASSIFICATION,
  CARD_TYPES,
  ADDITIONAL_INFORMATION,
  COST,
} from 'common/requestDetails/constants';

export class Simple extends React.Component {
  state = {
    lineItem: this.props.item,
    editMode: false,
  };
  pendingLineItem = null;
  lineItemUpdateComplete = false;

  toggleEditMode = () => this.setState({editMode: !this.state.editMode});

  dataWithIds(pr) {
    return produce(pr, (draft) => {
      if (_.get(draft, 'additionalInformation.changes')) {
        _.set(
          draft,
          'additionalInformation.changes',
          _.map(_.get(draft, 'additionalInformation.changes'), (c, idx) => {
            c.id = c.id || `${idx}-${generateTimeId()}`;
            return c;
          })
        );
      }
    });
  }

  componentDidMount() {
    if (this.props.currentlyCollaborating && !_.isEmpty(this.props.item)) {
      this.setState({lineItem: this.dataWithIds(this.props.item)});
    }
  }

  handleChange = (lineItem, prevPropsItem, currentlyCollaborating) => {
    return new Promise((resolve) => {
      if (currentlyCollaborating) {
        if (!_.isEqual(prevPropsItem, lineItem)) {
          this.pendingLineItem = produceAppliedDiff(
            prevPropsItem,
            lineItem,
            this.state?.lineItem
          );
          this.lineItemUpdateComplete = false;
          this.setState({lineItem: this.pendingLineItem}, () => {
            this.lineItemUpdateComplete = true; // Mark as complete but DON'T clear pendingLineItem yet
            resolve();
          });
        } else {
          resolve();
        }
      } else {
        this.setState({lineItem}, () => {
          resolve();
        });
      }
    });
  };

  componentDidUpdate(prevProps, prevState) {
    // Check if we have a pending update AND the callback has run
    // but the state doesn't match what we expected
    if (this.props.currentlyCollaborating) {
      if (
        this.pendingLineItem &&
        this.lineItemUpdateComplete &&
        !_.isEqual(this.state.lineItem, this.pendingLineItem)
      ) {
        this.lineItemUpdateComplete = false;
        // Force another state update
        this.setState({lineItem: this.pendingLineItem});
      } else if (this.lineItemUpdateComplete) {
        // Only clear pendingLineItem after componentDidUpdate runs
        // and we're certain the state is correct
        this.pendingLineItem = null;
      }
    }
  }

  toggleEditModeIneligibleDeferred = (value, editor) => {
    if (value && !editor) {
      this.toggleEditMode();
    }
  };

  handleBlur = (fieldName) => {
    if (this.props.editable) {
      let {lineItem} = this.state;
      let {item} = this.props;
      lineItem = _.cloneDeep(lineItem);
      let itemToDisplay = {
        ...lineItem,
        [ADDITIONAL_INFORMATION]: {
          ..._.get(item, ADDITIONAL_INFORMATION), // need to bring in additional information saved outside this component
          ..._.get(lineItem, ADDITIONAL_INFORMATION),
        },
      };

      this.setState((prevState) => ({
        lineItem: {
          ...prevState.lineItem,
          changes: itemToDisplay.changes,
          bocTotals: itemToDisplay.bocTotals,
        },
      }));
      this.props.saveItem(this.props.sourcePath, itemToDisplay, fieldName);
    }
  };

  handleItemChange = (name, value, ind = -1, extraArgs) => {
    const {item, itemConfig, originalItem} = this.props;
    const bundledCostValues = _.get(itemConfig, 'bundledCostValues');
    let attributeName = _.split(name, '.').pop();
    if (_.includes(bundledCostValues, attributeName)) {
      return this.handleBundledItemChange(attributeName, value);
    } else {
      let newLineItem = _.cloneDeep(item);
      _.set(newLineItem, name, value);
      if (_.get(extraArgs, 'resetFields')) {
        _.each(extraArgs.resetFields, (field) => {
          _.unset(newLineItem, field);
        });
      }
      newLineItem = this.getItemWithChanges(originalItem, newLineItem);
      if (!originalItem) {
        return this.handleChange(newLineItem, item, this.props.currentlyCollaborating);
      }
      return this.handleChange(newLineItem);
    }
  };

  handleBundledItemChange = (attributeName, value, ind = -1) => {
    let newLineItem = _.cloneDeep(this.props.item);
    let costs = _.get(newLineItem, COST);
    _.map(costs, (costArray) => {
      if (ind !== -1) {
        _.set(costArray[ind], attributeName, value);
      } else {
        _.map(costArray, (cost) => {
          _.set(cost, attributeName, value);
        });
      }
    });
    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    return this.handleChange(newLineItem);
  };

  handleIneligibleItemChange = (name, value, ind = -1) => {
    let oldItem = this.props.originalItem;
    let newItem = this.props.item;
    let justification = _.get(newItem, 'additionalInformation.justification');
    let newLineItem = oldItem ? _.cloneDeep(oldItem) : _.cloneDeep(newItem);
    _.set(newLineItem, name, value);
    // save current justification to prevent incorrect validation errors
    if (!!oldItem && justification) {
      _.set(newLineItem, 'additionalInformation.justification', justification);
    }
    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    return this.handleChange(newLineItem);
  };

  handleIneligibleItemChangeAndBlur = (name, value, fieldName) => {
    this.handleIneligibleItemChange(name, value).then((res) => {
      this.handleBlur(fieldName);
    });
    // if marking ineligible, should disable edit mode as only change should be that it's marked ineligible
    this.toggleEditModeIneligibleDeferred(value, !this.state.editMode);
  };

  handleItemChangeAndBlur = (name, value, fieldName) => {
    this.handleItemChange(name, value).then((res) => {
      this.handleBlur(fieldName);
    });
  };

  handleItemChangeAndEditMode = (name, value, fieldName) => {
    this.handleItemChangeAndBlur(name, value, fieldName);
    this.toggleEditModeIneligibleDeferred(value, this.state.editMode);
  };

  /*
   * Extract the additionalInformation.changes objects that are associated with the
   * additionalQuestions fields.
   */
  getAdditionalQuestionsChanges(newItemIn) {
    const {itemConfig} = this.props;
    const entityType = _.get(this.context, 'entityType');
    let reviewContents = _.get(itemConfig, 'additionalQuestions.reviewContents', []);
    if (entityType == 'amendment')
      reviewContents = _.get(itemConfig, 'additionalQuestions.amendmentContents', []);
    const changeParams = extractChangeParamsFromCfg(reviewContents);
    const additionalQuestionFieldNames = _.reduce(
      changeParams,
      function (result, value, key) {
        result.push(_.get(value, 'fieldName'));
        return result;
      },
      []
    );
    const originalChanges = _.get(newItemIn, 'additionalInformation.changes');
    const originalChangesToKeep = _.filter(originalChanges, (o) => {
      return _.includes(additionalQuestionFieldNames, _.get(o, 'field'));
    });
    return originalChangesToKeep;
  }

  getItemWithChanges = (oldItem, newItemIn) => {
    const {itemConfig} = this.props;
    const period = calculatePeriod(itemConfig);
    const bundledCostValues = itemConfig.bundledCostValues;
    const originalChangesToKeep = this.getAdditionalQuestionsChanges(newItemIn);
    let displayCostRow = _.get(oldItem, ['cost', 0], []).length > 1;
    let newItem = produce(newItemIn, (draft) => {
      return diffItem(
        draft,
        oldItem,
        _.get(itemConfig, 'labels'),
        this.props.itemCodeOptions,
        this.props.currentlyCollaborating
      );
    });

    let descriptionLabel = _.get(itemConfig, 'labels.description');
    let otherItemLabel = _.get(itemConfig, 'labels.otherDescription');
    let changes = [];
    let descriptionPath = 'additionalInformation.description';

    if (oldItem) {
      const findCost = (item, year, ind) => {
        return _.get(item, ['cost', year, ind], {});
      };
      if (!_.isEqual(_.get(oldItem, descriptionPath), _.get(newItem, descriptionPath))) {
        changes.push(
          createChangeObj(
            CHANGES_TYPES.VALUE_CHANGE_NO_DISPLAY,
            DESCRIPTION,
            undefined,
            undefined,
            descriptionLabel,
            this.props.currentlyCollaborating
          )
        );
      }

      if (!_.isEqual(_.get(oldItem, EXPLAIN_OTHER), _.get(newItem, EXPLAIN_OTHER))) {
        changes.push(
          createChangeObj(
            CHANGES_TYPES.VALUE_CHANGE,
            NAME,
            _.get(oldItem, EXPLAIN_OTHER),
            _.get(newItem, EXPLAIN_OTHER),
            otherItemLabel,
            this.props.currentlyCollaborating
          )
        );
      }

      let bocChanged = false;
      let quantityChanged = false;
      let priceChanged = false;
      let unitOfMeasureChanged = false;
      let preAwardChanged = false;
      let phasedProjectChanged = false;
      let costClassificationChanged = false;
      _.forEach(_.get(newItem, 'cost', []), (phaseCost, phaseInd) => {
        let phaseCostSize = phaseCost.length;
        _.forEach(phaseCost, (newCost, ind) => {
          const oldCost = findCost(oldItem, phaseInd, ind);
          const quantity = _.get(newCost, 'quantity');
          const price = _.get(newCost, 'price');
          const quantityOld = _.get(oldCost, 'quantity');
          const priceOld = _.get(oldCost, 'price');
          const added = _.get(newCost, 'added', false);
          let costRow = displayCostRow ? _.get(newCost, 'costRow', 0) : 0;
          if (added) {
            changes.push({
              ...createChangeObj(
                CHANGES_TYPES.NEWLY_CREATED,
                undefined,
                undefined,
                undefined,
                undefined,
                this.props.currentlyCollaborating
              ),
              costRow: costRow,
              periodType: period.type,
            });
          } else {
            let boc = _.get(newCost, 'boc');
            let bocOld = _.get(oldCost, 'boc');

            if (boc !== bocOld) {
              if (
                !_.includes(bundledCostValues, 'boc') ||
                (_.includes(bundledCostValues, 'boc') && !bocChanged)
              ) {
                changes.push({
                  ...createChangeObj(
                    CHANGES_TYPES.VALUE_CHANGE,
                    BOC,
                    BOC_NAMES[bocOld],
                    BOC_NAMES[boc],
                    undefined,
                    this.props.currentlyCollaborating
                  ),
                  costRow: costRow,
                  periodType: period.type,
                });
                bocChanged = true;
              }
            }

            if (_.toNumber(quantity) !== _.toNumber(quantityOld)) {
              if (
                !_.includes(bundledCostValues, 'quantity') ||
                (_.includes(bundledCostValues, 'quantity') && !quantityChanged)
              ) {
                let message = `${
                  phaseCostSize > 1 ? `cost ${ind + 1} ` : ''
                }${_.upperFirst(period.type)} ${phaseInd + 1} ${QUANTITY}`;
                changes.push({
                  ...createChangeObj(
                    CHANGES_TYPES.VALUE_CHANGE,
                    QUANTITY,
                    quantityOld,
                    quantity,
                    period.type !== 'whole' ? message : VALUE_CHANGE_DISPLAY[QUANTITY],
                    this.props.currentlyCollaborating
                  ),
                  costRow: costRow,
                  periodType: period.type,
                });
                quantityChanged = true;
              }
            }
            if (_.toNumber(price) !== _.toNumber(priceOld)) {
              if (
                !_.includes(bundledCostValues, 'price') ||
                (_.includes(bundledCostValues, 'price') && !priceChanged)
              ) {
                let message = `${
                  phaseCostSize > 1 ? `cost ${ind + 1} ` : ''
                }${_.upperFirst(period.type)} ${phaseInd + 1} ${PRICE}`;

                changes.push({
                  ...createChangeObj(
                    CHANGES_TYPES.VALUE_CHANGE,
                    PRICE,
                    priceOld,
                    price,
                    period.type !== 'whole' ? message : VALUE_CHANGE_DISPLAY[PRICE],
                    this.props.currentlyCollaborating
                  ),
                  costRow: costRow,
                  periodType: period.type,
                });
                priceChanged = true;
              }
            }

            const unitOfMeasure = _.get(newCost, 'unitOfMeasure');
            const unitOfMeasureOld = _.get(oldCost, 'unitOfMeasure');

            if (unitOfMeasure !== unitOfMeasureOld) {
              if (
                !_.includes(bundledCostValues, 'unitOfMeasure') ||
                (_.includes(bundledCostValues, 'unitOfMeasure') && !unitOfMeasureChanged)
              ) {
                changes.push({
                  ...createChangeObj(
                    CHANGES_TYPES.VALUE_CHANGE,
                    UNIT_OF_MEASURE,
                    unitOfMeasureOld,
                    unitOfMeasure,
                    undefined,
                    this.props.currentlyCollaborating
                  ),
                  costRow: costRow,
                  periodType: period.type,
                });
                unitOfMeasureChanged = true;
              }
            }

            const preAward = _.get(newCost, 'preAward', false);
            const preAwardOld = _.get(oldCost, 'preAward', false);

            if (preAward !== preAwardOld) {
              if (
                !_.includes(bundledCostValues, 'preAward') ||
                (_.includes(bundledCostValues, 'preAward') && !preAwardChanged)
              ) {
                changes.push({
                  ...createChangeObj(
                    CHANGES_TYPES.VALUE_CHANGE,
                    PRE_AWARD,
                    preAwardOld,
                    preAward,
                    undefined,
                    this.props.currentlyCollaborating
                  ),
                  costRow: costRow,
                  periodType: period.type,
                  formatType: VALUE_CHANGE_FORMAT.preAward,
                });
                preAwardChanged = true;
              }
            }

            const phasedProject = _.get(newCost, 'phasedProject', false);
            const phasedProjectOld = _.get(oldCost, 'phasedProject', false);

            if (phasedProject !== phasedProjectOld) {
              if (
                !_.includes(bundledCostValues, 'phasedProject') ||
                (_.includes(bundledCostValues, 'phasedProject') && !phasedProjectChanged)
              ) {
                changes.push({
                  ...createChangeObj(
                    CHANGES_TYPES.VALUE_CHANGE,
                    PHASED_PROJECT,
                    phasedProjectOld,
                    phasedProject,
                    undefined,
                    this.props.currentlyCollaborating
                  ),
                  costRow: costRow,
                  periodType: period.type,
                });
                phasedProjectChanged = true;
              }
            }

            const costClassification = _.get(newCost, 'costClassification');
            const costClassificationOld = _.get(oldCost, 'costClassification');

            if (costClassification !== costClassificationOld) {
              if (
                !_.includes(bundledCostValues, 'costClassification') ||
                (_.includes(bundledCostValues, 'costClassification') &&
                  !costClassificationChanged)
              ) {
                changes.push({
                  ...createChangeObj(
                    CHANGES_TYPES.VALUE_CHANGE,
                    COST_CLASSIFICATION,
                    costClassificationOld,
                    costClassification,
                    undefined,
                    this.props.currentlyCollaborating
                  ),
                  costRow: costRow,
                  periodType: period.type,
                });
                costClassificationChanged = true;
              }
            }
          }
        });
      });
    }

    let updatedItem = produce(newItem, (draft) => {
      _.set(
        draft,
        ['additionalInformation', 'changes'],
        _.concat(_.get(newItem, 'additionalInformation.changes'), changes)
      );
      _.set(
        draft,
        ['additionalInformation', 'changes'],
        _.concat(draft.additionalInformation.changes, originalChangesToKeep)
      );
    });

    return updatedItem;
  };

  renderChangesSection = () => {
    const {lineItem, editMode} = this.state;
    const {
      validationErrors,
      validationDirtyMap,
      changeLabel,
      justificationLabel,
      editable,
    } = this.props;
    const justification = _.get(lineItem, 'additionalInformation.justification');
    const changes = _.get(lineItem, 'additionalInformation.changes');
    const formattedSortedChanges = _.map(changes, (change) =>
      requestDetailsChangesToDisplayString(change)
    );
    const JUSTIFICATION = 'additionalInformation.justification';

    const justificationErrors = displayError(
      'justification',
      JUSTIFICATION,
      validationErrors,
      validationDirtyMap,
      false
    );

    const newlyCreated = _.find(
      _.get(lineItem, 'additionalInformation.changes', []),
      (change) => change.type === 'newlyCreated'
    );

    const ineligible = _.get(lineItem, 'additionalInformation.ineligible');

    return (
      <Grid className="diffingBackground change-section-grid">
        <Grid.Row>
          <Grid.Column computer={8} mobile={16}>
            <Header as="h5">{changeLabel ? changeLabel : 'Reviewer Edits'}</Header>
            <div>
              {_.map(formattedSortedChanges, (change, index) => {
                return <p key={index}>{change}</p>;
              })}
            </div>
          </Grid.Column>
          <Grid.Column computer={8} mobile={16}>
            {(editMode || newlyCreated || ineligible) && editable ? (
              <TextAreaField
                style={{width: '100%'}}
                value={justification}
                title={
                  justificationLabel
                    ? justificationLabel
                    : 'Justification (included in award package)'
                }
                label={
                  justificationLabel
                    ? justificationLabel
                    : 'Justification (included in award package)'
                }
                id={`${JUSTIFICATION}-${this.props.id}`}
                name={JUSTIFICATION}
                handleChange={(e, data) => {
                  const newLineItem = produce(lineItem, (draft) => {
                    _.set(draft, JUSTIFICATION, data.value);
                  });
                  this.handleChange(
                    newLineItem,
                    lineItem,
                    this.props.currentlyCollaborating
                  );
                }}
                handleBlur={() => this.handleBlur(JUSTIFICATION)}
                displayError={justificationErrors.displayError}
                errorMessages={justificationErrors.errorMessages}
              />
            ) : (
              <div>
                <Header as="h5">
                  {justificationLabel
                    ? justificationLabel
                    : 'Justification (included in award package)'}
                </Header>
                {justificationErrors.displayError ? (
                  <p className="errorMessages">{justificationErrors.errorMessages}</p>
                ) : (
                  justification
                )}
              </div>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  render() {
    return (
      <SimpleShared
        lineItem={this.state.lineItem}
        editMode={this.state.editMode}
        costType={CARD_TYPES.SIMPLE}
        toggleEditMode={this.toggleEditMode}
        handleItemChangeAndEditMode={this.handleItemChangeAndEditMode}
        renderChangesSection={this.renderChangesSection}
        handleChange={this.handleChange}
        handleBlur={this.handleBlur}
        handleItemChange={this.handleItemChange}
        handleItemChangeAndBlur={this.handleItemChangeAndBlur}
        handleIneligibleItemChange={this.handleIneligibleItemChange}
        handleIneligibleItemChangeAndBlur={this.handleIneligibleItemChangeAndBlur}
        {...this.props}
      />
    );
  }
}

export const mapStateToProps = (state, props) => {
  const currentlyCollaborating = !!_.get(
    state,
    'dynamicComponent.collaboration.roomList',
    ''
  );
  return {currentlyCollaborating};
};

export default connect(mapStateToProps, null)(Simple);
